import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Common components
import Navigation from './components/common/Navigation';
import Footer from './components/common/Footer';

// Public pages
import LandingPage from './pages/public/LandingPage';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import DeleteAccount from './pages/public/DeleteAccount';
import TermsOfUse from './pages/public/TermsOfUse';
import CragRedirect from './pages/public/CragRedirect';

import './App.css';

// Admin Components - Lazy loaded (code splitting)
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'));

// Admin Pages - Lazy loaded
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Users = lazy(() => import('./pages/admin/Users'));
const Models = lazy(() => import('./pages/admin/Models'));
const UploadModel = lazy(() => import('./pages/admin/UploadModel'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const Crags = lazy(() => import('./pages/admin/Crags'));
const ModelDetail = lazy(() => import('./pages/admin/ModelDetail'));
const AdminCragDetail = lazy(() => import('./pages/admin/CragDetail'));
const Labelling = lazy(() => import('./pages/admin/Labelling'));
const Scans = lazy(() => import('./pages/admin/Scans'));
const InfoAnnotations = lazy(() => import('./pages/admin/InfoAnnotations'));

// Contributor Components - Lazy loaded (completely separate from admin)
const ContributorLogin = lazy(() => import('./components/contributor/ContributorLogin'));
const MagicLinkVerify = lazy(() => import('./components/contributor/MagicLinkVerify'));
const ContributorLayout = lazy(() => import('./components/contributor/ContributorLayout'));
const ContributorRoute = lazy(() => import('./components/contributor/ContributorRoute'));
const MyModelsAndEdits = lazy(() => import('./pages/contributor/MyModelsAndEdits'));
const AddModelWizard = lazy(() => import('./pages/contributor/AddModelWizard'));
const ContributorCragEdit = lazy(() => import('./pages/contributor/ContributorCragEdit'));

// Loading fallback for admin routes
const AdminLoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f5f5'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #e0e0e0',
        borderTop: '4px solid #F84982',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }} />
      <p style={{ color: '#666' }}>Loading...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isContributorRoute = location.pathname.startsWith('/contributor');
  const isLandingPage = location.pathname === '/';
  const isLegalPage = location.pathname === '/privacy-policy' || location.pathname === '/terms-of-use' || location.pathname === '/delete-account';
  const hideNavFooter = isAdminRoute || isContributorRoute || isLandingPage || isLegalPage;

  return (
    <div className="App">
      {!hideNavFooter && <Navigation />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/crag/:id" element={<CragRedirect />} />

        {/* Admin Routes - Lazy loaded with Suspense */}
        <Route path="/admin" element={
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminLogin />
          </Suspense>
        } />
        <Route path="/admin/*" element={
          <Suspense fallback={<AdminLoadingFallback />}>
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          </Suspense>
        }>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="models" element={<Models />} />
          <Route path="models/upload" element={<UploadModel />} />
          <Route path="models/:id" element={<ModelDetail />} />
          <Route path="crags" element={<Crags />} />
          <Route path="crags/:id" element={<AdminCragDetail />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="labelling" element={<Labelling />} />
          <Route path="scans" element={<Scans />} />
          <Route path="info-annotations" element={<InfoAnnotations />} />
        </Route>

        {/* Contributor Routes - Lazy loaded (completely separate from admin) */}
        <Route path="/contributor" element={
          <Suspense fallback={<AdminLoadingFallback />}>
            <ContributorLogin />
          </Suspense>
        } />
        <Route path="/contributor/magic-link" element={
          <Suspense fallback={<AdminLoadingFallback />}>
            <MagicLinkVerify />
          </Suspense>
        } />
        <Route path="/contributor/*" element={
          <Suspense fallback={<AdminLoadingFallback />}>
            <ContributorRoute>
              <ContributorLayout />
            </ContributorRoute>
          </Suspense>
        }>
          <Route path="models" element={<MyModelsAndEdits />} />
          <Route path="add-model" element={<AddModelWizard />} />
          <Route path="crags/:id" element={<ContributorCragEdit />} />
        </Route>

        {/* Catch-all: redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideNavFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
